import type { PortableTextBlock } from '@portabletext/types';
import type { Client } from 'openapi-fetch';

import type { components } from '../types/api/generated';
import type { PlannerGameweek } from '@api-service/planner';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';
import type { SanityPredictedLineupTeamSection } from '@lib/sanity';

export type ContentVideo =
  components['schemas']['VideosResponse']['data'][number];
// The OpenAPI schema types Portable Text as an open object; the renderer and
// the block hydrator both want the real block type.
export type PremiumBodyBlock = PortableTextBlock;

/**
 * Which editorial document to read. A team reveal is addressed by its
 * contributor's handle as well as its slug, matching the URL it renders under.
 */
export type ContentDocumentSelector =
  | { type: 'articlePage'; slug: string }
  | { type: 'teamReveal'; authorSlug: string; slug: string };

const FORBIDDEN = 403;
const NOT_FOUND = 404;

/**
 * Fetches premium (members-only) post content from the ffh-backend by slug.
 * Requires a valid paid entitlement, which the backend enforces after reading
 * the offloaded content. Returns undefined when the member is not entitled
 * (403) or the post has no offloaded content (404), mirroring scoreline-app
 * so the page simply keeps showing the preview.
 */
export const fetchPost = async (
  slug: string,
  accessToken: string
): Promise<string | undefined> => {
  const { data, response } = await ffhClient().GET('/content/posts/{slug}', {
    params: { path: { slug } },
    headers: authHeader(accessToken)
  });

  if (response.status === FORBIDDEN || response.status === NOT_FOUND) {
    return undefined;
  }

  if (!data) {
    throw new Error(`Failed to fetch post: ${response.status}`);
  }

  const { rendered } = data.data;
  if (rendered == null) return undefined;
  return typeof rendered === 'string' ? rendered : JSON.stringify(rendered);
};

/**
 * Asks the API for Team boxes as they are. Without it each box arrives as a
 * plain image of its screenshot, which is what app builds that predate the box
 * get.
 */
const TEAM_BOXES_INCLUDE = 'teamBoxes';

/** `drafts` reads the box as the editor preview shows it. */
export type RevealTeamPerspective = 'published' | 'drafts';

export type RevealTeamBoxSelector = Extract<
  ContentDocumentSelector,
  { type: 'teamReveal' }
> & {
  /** The box's `_key` in the premium body. */
  blockKey: string;
  perspective: RevealTeamPerspective;
};

/**
 * The endpoints the Team box needs, typed by hand in the generated schema's
 * shape because the backend has not shipped them yet: the team read, and the
 * premium body's `include=teamBoxes`. Once they are in types/api/openapi.json,
 * delete this and the casts that use it.
 */
type ProposedContentPaths = {
  '/content/team-reveals/{author}/{slug}/premium': {
    parameters: {
      query?: never;
      header?: never;
      path?: never;
      cookie?: never;
    };
    get: {
      parameters: {
        query?: { include?: typeof TEAM_BOXES_INCLUDE };
        header?: never;
        path: { author: string; slug: string };
        cookie?: never;
      };
      requestBody?: never;
      responses: {
        200: {
          headers: { [name: string]: unknown };
          content: {
            'application/json': components['schemas']['ArticlePremiumBodyResponse'];
          };
        };
      };
    };
  };
  '/content/team-reveals/{author}/{slug}/team/{block}': {
    parameters: {
      query?: never;
      header?: never;
      path?: never;
      cookie?: never;
    };
    get: {
      parameters: {
        query?: { perspective?: RevealTeamPerspective };
        header?: never;
        path: { author: string; slug: string; block: string };
        cookie?: never;
      };
      requestBody?: never;
      responses: {
        200: {
          headers: { [name: string]: unknown };
          content: { 'application/json': { data: PlannerGameweek } };
        };
      };
    };
  };
};

/**
 * Fetches the premium body of a Sanity article or team reveal as Portable
 * Text. The API enforces the PRO or ULTRA entitlement. Returns undefined when
 * the member is not entitled (403) or the document has no premium body (404),
 * so the page keeps showing the teaser and paywall rather than blanking.
 */
export const fetchPremiumBody = async (
  selector: ContentDocumentSelector,
  accessToken: string
): Promise<PremiumBodyBlock[] | undefined> => {
  const { data, response } =
    selector.type === 'articlePage'
      ? await ffhClient().GET('/content/articles/{slug}/premium', {
          params: { path: { slug: selector.slug } },
          headers: authHeader(accessToken)
        })
      : await (ffhClient() as unknown as Client<ProposedContentPaths>).GET(
          '/content/team-reveals/{author}/{slug}/premium',
          {
            params: {
              path: { author: selector.authorSlug, slug: selector.slug },
              query: { include: TEAM_BOXES_INCLUDE }
            },
            headers: authHeader(accessToken)
          }
        );

  if (response.status === FORBIDDEN || response.status === NOT_FOUND) {
    return undefined;
  }

  if (!data) {
    throw new Error(`Failed to fetch premium body: ${response.status}`);
  }

  return data.data.body as unknown as PremiumBodyBlock[];
};

/**
 * Fetches the planned team for one team box: the plan of the Hub planner
 * linked to the FPL ID the contributor entered, as one planner gameweek. Same
 * gate as the premium body (PRO or ULTRA, enforced by the API). Any refusal
 * comes back as undefined.
 */
export const fetchRevealPlannedTeam = async (
  selector: RevealTeamBoxSelector,
  accessToken: string
): Promise<PlannerGameweek | undefined> => {
  const client = ffhClient() as unknown as Client<ProposedContentPaths>;
  const { data } = await client.GET(
    '/content/team-reveals/{author}/{slug}/team/{block}',
    {
      params: {
        path: {
          author: selector.authorSlug,
          slug: selector.slug,
          block: selector.blockKey
        },
        query: { perspective: selector.perspective }
      },
      headers: authHeader(accessToken)
    }
  );
  return data?.data;
};

/**
 * Fetches the YouTube video list surfaced by the ffh-backend. Public endpoint
 * (no auth), cached upstream.
 */
export const fetchVideos = async (): Promise<ContentVideo[]> => {
  const { data, response } = await ffhClient().GET('/content/videos');

  if (!data) {
    throw new Error(`Failed to fetch videos: ${response.status}`);
  }

  return data.data;
};

/**
 * Fetches the gated club sections of the predicted lineups page (FFH-2919).
 * Same contract as the premium body: the API enforces PRO or ULTRA, and a 403
 * or 404 comes back as undefined so the page keeps the pitches and shows the
 * paywall in place of the words.
 */
export const fetchPremiumTeamSections = async (
  accessToken: string
): Promise<SanityPredictedLineupTeamSection[] | undefined> => {
  const { data, response } = await ffhClient().GET(
    '/content/predicted-lineups/premium',
    { headers: authHeader(accessToken) }
  );

  if (response.status === FORBIDDEN || response.status === NOT_FOUND) {
    return undefined;
  }

  if (!data) {
    throw new Error(
      `Failed to fetch premium club sections: ${response.status}`
    );
  }

  return data.data.teams as unknown as SanityPredictedLineupTeamSection[];
};
